import Route from '@ember/routing/route'
import RSVP from 'rsvp'
import { service } from '@ember/service'
import type Store from '@ember-data/store'
import type SessionService from 'teacher-ui-client/services/session'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type Teacher from 'teacher-ui-client/models/teacher'
import type ReportingUiScopeRoute from 'teacher-ui-client/reporting/ui-scope/route'
import type { NormalizedInterval } from 'date-fns'
import type StudentEventsService from 'teacher-ui-client/services/student-events'
import type { SchoolScope, StudentsScope } from 'teacher-ui-client/reporting/ui-scope/utils'
import { hasId } from 'teacher-ui-client/utils/type-guards'

export default class ReportingReSummaryTableRoute extends Route {
  @service studentEvents!: StudentEventsService

  @service session!: SessionService

  @service store!: Store

  get isSubco() {
    return this.session.currentTeacher.subcoFor(SubscriptionType.Reading)
  }

  // used by teachers tab, has no scope selector, ignores scoped data
  get readingTeachers() {
    return this.store
      .peekAll<Teacher>('teacher')
      .filter((teacher) => teacher.hasSchoolClassForSubscriptionType(SubscriptionType.Reading))
  }

  async model() {
    const product: LegacyProductRe = 're'
    const { period, scopedData } = this.modelFor('reporting.ui-scope') as ModelFor<ReportingUiScopeRoute>

    const scopedStudents = scopedData.students
    const scopes: { school: SchoolScope; students: StudentsScope } = {
      school: { scope: 'school', ids: [this.session.currentSchool.id] },
      students: { scope: 'students', ids: scopedStudents.map((s) => s.id) },
    }

    const stats = await this.makeStatsRequests(product, scopes, period)
    return {
      product,
      students: scopedStudents,
      readingTeachers: this.readingTeachers,
      ...stats,
    }
  }

  /**
   * Simple Summary endpoint is requested twice for subco users as they
   * need to display both students and teachers summaries.
   */
  makeStatsRequests = async (
    product: LegacyProductRe,
    scopes: { school: SchoolScope; students: StudentsScope },
    period: NormalizedInterval,
  ) => {
    const requestData = this.getTeachersDataForSimpleSummary(this.readingTeachers)

    return RSVP.hash({
      schoolSimpleSummary: this.isSubco
        ? this.studentEvents.simpleSummarySchool(product, scopes.school, period, requestData)
        : null,
      studentsSimpleSummary: this.studentEvents.simpleSummaryStudents(product, scopes.students, period),
    })
  }

  /**
   * Prepares data for the simple summary student events method
   * with teachers and their students
   */
  getTeachersDataForSimpleSummary(teachers: Teacher[]) {
    return {
      teachers: teachers.filter(hasId).map((teacher) => ({
        teacher_id: teacher.id,
        student_ids: teacher
          .studentsForSubscriptionType(SubscriptionType.Reading)
          .filter(hasId)
          .map((student) => student.id),
      })),
    }
  }
}
